import { createSlice } from "@reduxjs/toolkit"
import isEqual from "lodash.isequal"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { frozenStateReconciler } from "../../../../Utils/persistHelpers"
import { logout } from "../../../LoginPage/redux/auth.actions"
import { preferencesSuccess } from "../../../User/redux/user.actions"
import { REQUESTS_INTEGRATIONS_SLICE_NAME } from "./requestsIntegrations.constants"
import {
  deleteRequestsIntegration,
  getRequestsIntegrations,
  getRequestsIntegrationsExport
} from "./requestsIntegrations.thunks"

/* ------------- Initial State ------------- */

const columnsAdmin = [
  { field_id: "created_timestamp", label: "Request date", active: true },
  { field_id: "integration_url", label: "Integration URL", active: true },
  { field_id: "description", label: "Description", active: true },

  { field_id: "user_full_name", label: "User full name", active: true },
  { field_id: "user_email", label: "User email", active: true },
  { field_id: "business_account", label: "Business account", active: true },
  { field_id: "business_url", label: "Business URL", active: true },

  { field_id: "admin_comment", label: "Comment", active: true },
  { field_id: "admin_screenshots", label: "Screenshots", active: true }
]

const initialState = {
  fetching: false,
  result: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  defaultColumnsAdmin: [...columnsAdmin],
  columnsAdmin: [...columnsAdmin],

  filtersLastUpd: null,
  filters: {
    created_timestamp: null,
    integration_url: [],
    description: [],
    user_full_name: [],
    user_email: [],
    business_account: [],
    business_url: [],
    exclude_fields: [],
    admin_comment: [],
    admin_screenshots: "all"
  },

  visibleFiltersAdmin: { user_email: true, user_full_name: true, business_account: true },

  exportFetching: false,
  exportResult: null
}

const slice = createSlice({
  name: REQUESTS_INTEGRATIONS_SLICE_NAME,
  initialState,
  reducers: {
    // Legacy call sites dispatched this with two arguments —
    // `requestsIntegrationsSetProp(key, value)`. RTK actions carry a single payload, so
    // callers pass `requestsIntegrationsSetProp({ key, value })`, matching the
    // `requestsAutoRefundSetProp`/`requestsGiftsSetProp` slices.
    requestsIntegrationsSetProp(state, action) {
      const { key, value } = action.payload
      if (["filters", "visibleFilters", "visibleFiltersAdmin"]?.includes(key)) {
        if (key === "filters") state.filtersLastUpd = new Date().getTime()
        state[key] = { ...state?.[key], ...value }
      } else {
        state[key] = value
      }
    },
    clearRequestsIntegrationsFilters(state) {
      state.filters = initialState.filters
    }
  },
  extraReducers: (builder) => {
    builder
      /* ---------------- list ---------------- */
      .addCase(getRequestsIntegrations.pending, (state) => {
        state.fetching = true
      })
      .addCase(getRequestsIntegrations.fulfilled, (state, action) => {
        const result = action.payload
        state.fetching = false
        if (result?.results) {
          state.result = result
        } else {
          state.result = {
            count: result?.length || 0,
            results: result
          }
        }
      })
      .addCase(getRequestsIntegrations.rejected, (state) => {
        state.fetching = false
      })

      /* ---------------- delete ---------------- */
      .addCase(deleteRequestsIntegration.pending, (state) => {
        state.fetching = true
      })
      .addCase(deleteRequestsIntegration.fulfilled, (state, action) => {
        const { id } = action.payload
        state.fetching = false
        state.result.results = state.result.results?.filter((el) => el.id !== id)
        state.result.count = state.result.count - 1
      })
      .addCase(deleteRequestsIntegration.rejected, (state) => {
        state.fetching = false
      })

      /* ---------------- export ---------------- */
      .addCase(getRequestsIntegrationsExport.pending, (state) => {
        state.exportFetching = true
      })
      .addCase(getRequestsIntegrationsExport.fulfilled, (state, action) => {
        state.exportFetching = false
        state.exportResult = action.payload
      })
      .addCase(getRequestsIntegrationsExport.rejected, (state) => {
        state.exportFetching = false
      })

      /* ---------------- logout ---------------- */
      .addCase(logout, (state) => ({
        ...initialState,
        filters: state.filters,
        columnsAdmin: state.columnsAdmin,
        visibleFiltersAdmin: state.visibleFiltersAdmin
      }))

      /* ---------------- restore saved preferences ---------------- */
      .addCase(preferencesSuccess, (state, { payload }) => {
        const preference = payload?.["requests-integrations"]?.data
        if (!preference || preference.version !== REQUESTS_INTEGRATIONS_PREFERENCES_VERSION)
          return
        if (!preference.filters || !preference.columnsAdmin || !preference.visibleFiltersAdmin)
          return

        state.filters = preference.filters
        state.columnsAdmin = preference.columnsAdmin
        state.visibleFiltersAdmin = preference.visibleFiltersAdmin
      })
  }
})

export const requestsIntegrationsActions = slice.actions

// Bump when the shape of the saved `{ version, filters, columnsAdmin, visibleFiltersAdmin }`
// payload changes.
export const REQUESTS_INTEGRATIONS_PREFERENCES_VERSION = 1

/* ------------- Selectors ------------- */

export const selectAreFiltersDefault = (state) =>
  isEqual(state.requestsIntegrations.filters, initialState.filters)

const PERSISTED_REDUCER_VERSION = 2

export const persistedReducer = persistReducer(
  {
    key: "requestsIntegrations",
    storage,
    // Only the user's own table setup survives a reload — the requests list and export
    // result are re-fetched or transient.
    whitelist: ["filters", "columnsAdmin", "visibleFiltersAdmin"],
    stateReconciler: frozenStateReconciler,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
